














































































































































import CopyToClipboardButton from '@/app/components/CopyToClipboardButton.vue';
import { useFeatureFlags, useFilters } from '@/app/composable';
import store from '@/app/store';
import { Icon } from '@iconify/vue2';
import { PropType, Ref, computed, defineComponent } from '@vue/composition-api';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import utc from 'dayjs/plugin/utc';
import * as R from 'ramda';
import { useAssetRetrieval } from '../composable/asset-retrieval';
import { useAssetStatus } from '../composable/asset-status';
import { AssetType, AssetTypeId, ModelSourceOptions, StatusCode } from '../constants';
import { Asset } from '../types';
import AssetTypeIcon from './AssetTypeIcon.vue';
dayjs.extend(utc);
dayjs.extend(minMax);

export default defineComponent({
    name: 'AssetOverview',
    components: {
        Icon,
        AssetTypeIcon,
        CopyToClipboardButton,
    },
    props: {
        asset: {
            type: Object as PropType<Asset>,
            required: true,
        },
        filter: {
            type: String,
            required: true,
        },
        alert: {
            type: Object as PropType<{ entityId: string | number; alertId: string; enabled: boolean }>,
            required: false,
        },
        contract: {
            type: Object as PropType<{ id: string; offlineRetention: string[] }>,
            default: null,
        },
        acquiredAssetIds: {
            type: Array as PropType<number[]>,
            default: () => [],
        },
    },
    setup(props, { emit, root }) {
        const { flag } = useFeatureFlags();
        const isAlertsEnabled = flag('alerts');

        const user = computed(() => store.state.auth.user);
        const isAssetCreator = computed(() => user.value && user.value.id === props.asset.createdBy.id);
        const model = computed(() => props.asset && props.asset.assetTypeId === AssetTypeId.Model);

        const updatedDate = computed(() =>
            props.asset.modifiedAt
                ? dayjs.max([dayjs(props.asset.modifiedAt), dayjs(props.asset.updatedAt)]).toString()
                : props.asset.updatedAt,
        );

        const { formatDateTime } = useFilters();

        const assetType = computed(() => {
            switch (props.asset.assetTypeId) {
                case AssetTypeId.Model:
                    return {
                        type: AssetType.Model,
                        class: 'text-cherry-700',
                    };
                case AssetTypeId.Result:
                    return {
                        type: AssetType.Result,
                        class: 'text-blue-800',
                    };
                default:
                    return {
                        type: AssetType.Dataset,
                        class: 'text-orange-800',
                    };
            }
        });

        const allFilterEnabled = computed(() => props.filter === 'all');

        const status = computed(() => props.asset?.status);
        const contract = computed(() => props.contract);
        const acquiredAssetIds = computed(() => props.acquiredAssetIds);
        const asset: Ref<Asset> = computed<Asset>(() => props.asset);
        const {
            iconColour: statusIconColour,
            colour: statusClass,
            icon: statusIcon,
            label: statusLabel,
        } = useAssetStatus(status);
        const {
            isRetrievalDisabled,
            retrievalTooltip,
            accessibilityKeys,
            belongsToUserOrOrganisation,
            canRetrieve,
            isAcquiredAndNotOther,
        } = useAssetRetrieval(asset, user, contract, acquiredAssetIds);

        const isModel = computed(() => asset.value?.assetTypeId === AssetTypeId.Model);
        const isUploadedModel = computed(
            () => isModel.value && asset.value?.metadata?.model.source === ModelSourceOptions.uploaded,
        );

        const provenanceLinkTooltip = computed(() => {
            if (isUploadedModel.value) return 'The asset was uploaded by the User';

            const pipelineType = props.asset.assetTypeId === AssetTypeId.Dataset ? 'Data Check-in' : 'Analytics';
            if (props.asset.provenanceAssetWorkflowIds?.length === 0)
                return `The ${pipelineType} Pipeline, which created this asset, has been deleted.`;

            return `Navigate to ${pipelineType} Pipeline`;
        });

        const retrieve = () => {
            root.$router.push({
                name: 'retrieval:create',
                params: {
                    assetId: `${props.asset.id}`,
                    accessibility: accessibilityKeys.value,
                    contractId: contract.value?.id,
                    acquired: isAcquiredAndNotOther.value,
                    backTo: 'assets',
                } as any,
            });
        };

        const editAlert = () => {
            if (props.alert)
                root.$router.push({ name: 'alerts:edit', params: { id: props.alert.alertId, backTo: 'assets' } });
        };

        return {
            dayjs,
            emit,
            statusClass,
            isAlertsEnabled,
            statusIconColour,
            user,
            model,
            StatusCode,
            status,
            isAssetCreator,
            allFilterEnabled,
            AssetType,
            assetType,
            provenanceLinkTooltip,
            R,
            retrieve,
            retrievalTooltip,
            isRetrievalDisabled,
            belongsToUserOrOrganisation,
            formatDateTime,
            updatedDate,
            isUploadedModel,
            editAlert,
            statusIcon,
            statusLabel,
            canRetrieve,
            AssetTypeId,
        };
    },
});
